import { useEffect, useState } from 'react';

import { trim } from 'lodash-es';

import { CancelSaveButtons } from '@components/cancel-save-buttons';
import { TextField } from '@components/text-field';
import { TextArea } from '@components/textarea';

import { useUpdateCompanySocialData } from 'api/companies/hooks';
import { ImageInput } from 'components/image-input';
import { SettingsSection } from 'components/settings-section';
import { useStore } from 'models/Provider';
import { Company } from 'types/companies';

type CareerSocialProps = {
  companyInfo: Company;
};

export function CareerSocial({ companyInfo }: CareerSocialProps) {
  const { t } = useStore();

  const metaTitle = companyInfo.theme.meta?.property['og:title'] || '';
  const metaDescription = companyInfo.theme.meta?.property['og:description'] || '';
  const metaImage = companyInfo.theme.meta?.property['og:image'];
  const updateSocialImage = useUpdateCompanySocialData({
    successMessage: t('career.update.image.success'),
    errorMessage: t('career.update.image.error'),
  });
  const updateDescriptionInfo = useUpdateCompanySocialData({
    successMessage: t('company.settings.career.update.description.success'),
    errorMessage: t('company.settings.career.update.description.error'),
  });

  const [descriptionError, setDescriptionError] = useState(Boolean);
  const [titleError, setTitleError] = useState(Boolean);

  const defaultDraft = {
    title: metaTitle,
    description: metaDescription,
  };

  const [descriptionDraft, setDescriptionDraft] = useState(defaultDraft);

  const isDirty =
    descriptionDraft.description !== defaultDraft.description.replace(/\r\n/g, '\n') ||
    trim(descriptionDraft.title).localeCompare(trim(defaultDraft.title)) !== 0;

  useEffect(() => {
    if (descriptionDraft.description) {
      setDescriptionError(false);
    }
    if (descriptionDraft.title) {
      setTitleError(false);
    }
  }, [descriptionDraft]);

  function isDescriptionError() {
    if (!descriptionDraft.description || !descriptionDraft.title) {
      if (!descriptionDraft.description) {
        setDescriptionError(true);
      }
      if (!descriptionDraft.title) {
        setTitleError(true);
      }
      return true;
    }
    return false;
  }

  return (
    <>
      <SettingsSection
        title={t('company.settings.career.image')}
        description={t('company.settings.career.image.helptext')}
        hasSeparator
      >
        <ImageInput
          key={metaImage}
          url={metaImage}
          maxWidth={1200}
          onSave={(image) => updateSocialImage.mutate({ image })}
        />
      </SettingsSection>

      <SettingsSection
        title={t('company.settings.career.description')}
        description={t('company.settings.career.description.helptext')}
        isBordered
      >
        <TextField
          error={titleError && t('company.settings.career.save.title.error')}
          value={descriptionDraft.title}
          onChange={(value) => {
            setDescriptionDraft({ ...descriptionDraft, title: value });
          }}
          label={t('company.settings.career.social.title.label')}
        />
        <TextArea
          label={t('company.settings.career.social.description.label')}
          error={descriptionError && t('company.settings.career.save.description.error')}
          value={descriptionDraft.description}
          onChange={(value) => {
            setDescriptionDraft({ ...descriptionDraft, description: value });
          }}
        />
        <CancelSaveButtons
          isOpen={isDirty}
          onCancel={() => setDescriptionDraft(defaultDraft)}
          isDisabled={updateDescriptionInfo.isLoading}
          onSave={() => {
            if (!isDescriptionError()) {
              updateDescriptionInfo.mutate(descriptionDraft);
            }
          }}
        />
      </SettingsSection>
    </>
  );
}
